/**
 * annotations-renderer.js - Advanced Annotation Rendering System
 *
 * Provides comprehensive annotation capabilities for CFD visualizations:
 * - Text annotations with 3D positioning
 * - Arrow annotations (leader lines)
 * - Distance/angle/area measurements
 * - Scale bars and coordinate axes
 * - Interactive editing and management
 */

class AnnotationRenderer {
    constructor(scene, camera, renderer, domElement, engine = null) {
        this.scene = scene;
        this.camera = camera;
        this.renderer = renderer;
        this.domElement = domElement;
        this.engine = engine;

        // Annotation storage
        this.annotations = new Map();
        this.annotationObjects = new Map();

        // CSS2D Renderer for text labels
        this.labelRenderer = null;
        this.initLabelRenderer();

        // Interaction state
        this.selectedAnnotation = null;
        this.isEditing = false;

        // Material cache
        this.materials = {
            arrow: new THREE.MeshBasicMaterial({ color: 0xff0000 }),
            line: new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 2 }),
            measurement: new THREE.LineBasicMaterial({ color: 0x00ff00, linewidth: 2 })
        };

        // Animation callbacks for dynamic marker scaling
        this.markerScaleCallbacks = new Map();
    }

    /**
     * Initialize CSS2D renderer for text labels
     */
    initLabelRenderer() {
        // Create CSS2D renderer container
        const labelContainer = document.createElement('div');
        labelContainer.id = 'annotation-labels';
        labelContainer.style.position = 'absolute';
        labelContainer.style.top = '0';
        labelContainer.style.left = '0';
        labelContainer.style.width = '100%';
        labelContainer.style.height = '100%';
        labelContainer.style.pointerEvents = 'none';
        labelContainer.style.zIndex = '1000';

        this.domElement.parentElement.appendChild(labelContainer);
        this.labelRenderer = labelContainer;
    }

    /**
     * Create a screen-space sized marker that maintains consistent visual size
     * @param {Array} position - [x, y, z] position
     * @param {number} color - Hex color
     * @param {number} targetPixels - Desired size in pixels (default 8)
     * @param {string} annotationId - Annotation ID for cleanup
     */
    createScreenSpaceMarker(position, color, targetPixels = 8, annotationId) {
        const pointPosition = new THREE.Vector3(...position);
        const cameraDistance = this.camera.position.distanceTo(pointPosition);

        const viewportHeight = this.domElement ? this.domElement.clientHeight : 800;
        const fov = this.camera.fov * Math.PI / 180;
        const markerSize = (targetPixels / viewportHeight) * cameraDistance * 2 * Math.tan(fov / 2);

        // Create marker
        const markerGeometry = new THREE.SphereGeometry(markerSize, 16, 16);
        const markerMaterial = new THREE.MeshBasicMaterial({
            color: color,
            transparent: true,
            opacity: 0.5,
            depthTest: true,
            depthWrite: true
        });

        const marker = new THREE.Mesh(markerGeometry, markerMaterial);
        marker.position.copy(pointPosition);
        marker.userData.annotationId = annotationId;
        marker.userData.baseSize = markerSize;

        // Add dynamic scaling callback
        const scaleCallback = () => {
            if (marker && marker.parent) {
                const distance = this.camera.position.distanceTo(pointPosition);
                const newSize = (targetPixels / viewportHeight) * distance * 2 * Math.tan(fov / 2);
                const scale = newSize / markerSize;
                marker.scale.set(scale, scale, scale);
            }
        };

        // Store callback reference for cleanup
        if (annotationId) {
            const callbacks = this.markerScaleCallbacks.get(annotationId) || [];
            callbacks.push(scaleCallback);
            this.markerScaleCallbacks.set(annotationId, callbacks);
        }

        // Register callback with animation loop
        if (this.engine) {
            this.engine.addAnimationCallback(scaleCallback);
        }

        return marker;
    }

    /**
     * Add text annotation
     */
    addTextAnnotation(data) {
        const { id, text, position, font_size, color, background_color, background_opacity } = data;

        // Create HTML label element
        const labelDiv = document.createElement('div');
        labelDiv.className = 'annotation-label';
        labelDiv.textContent = text;
        labelDiv.style.position = 'absolute';
        labelDiv.style.fontSize = `${font_size}px`;
        labelDiv.style.color = this.colorToCSS(color);
        labelDiv.style.fontFamily = 'Arial, sans-serif';
        labelDiv.style.padding = '4px 8px';
        labelDiv.style.pointerEvents = 'auto';
        labelDiv.style.cursor = 'pointer';
        labelDiv.style.userSelect = 'none';

        if (background_color) {
            const bgColor = this.colorToCSS(background_color);
            labelDiv.style.backgroundColor = `rgba(${this.colorToRGB(background_color).join(',')}, ${background_opacity})`;
            labelDiv.style.borderRadius = '4px';
        }

        // Store position for projection
        const pos = new THREE.Vector3(...position);

        // Create screen-space sized marker point for 3D position
        const marker = this.createScreenSpaceMarker(position, this.colorToHex(color), 8, id);
        this.scene.add(marker);

        // Add click handler
        labelDiv.addEventListener('click', () => {
            this.selectAnnotation(id);
        });

        // Store annotation
        this.labelRenderer.appendChild(labelDiv);
        this.annotations.set(id, data);
        this.annotationObjects.set(id, {
            type: 'text',
            label: labelDiv,
            marker: marker,
            position: pos
        });

        return id;
    }

    /**
     * Add arrow annotation
     */
    addArrowAnnotation(data) {
        const { id, start_point, end_point, text, arrow_size, color, line_width } = data;

        const start = new THREE.Vector3(...start_point);
        const end = new THREE.Vector3(...end_point);
        const direction = new THREE.Vector3().subVectors(end, start);
        const length = direction.length();

        // Create arrow helper
        const arrowColor = this.colorToHex(color);
        const arrow = new THREE.ArrowHelper(
            direction.normalize(),
            start,
            length,
            arrowColor,
            arrow_size * length,
            arrow_size * length * 0.5
        );
        arrow.userData.annotationId = id;

        // Add to scene
        this.scene.add(arrow);

        // Add text label if provided
        let label = null;
        if (text) {
            const labelDiv = document.createElement('div');
            labelDiv.className = 'annotation-label';
            labelDiv.textContent = text;
            labelDiv.style.position = 'absolute';
            labelDiv.style.fontSize = '12px';
            labelDiv.style.color = this.colorToCSS(color);
            labelDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
            labelDiv.style.padding = '2px 6px';
            labelDiv.style.borderRadius = '3px';
            labelDiv.style.pointerEvents = 'auto';
            labelDiv.style.cursor = 'pointer';

            labelDiv.addEventListener('click', () => {
                this.selectAnnotation(id);
            });

            this.labelRenderer.appendChild(labelDiv);
            label = labelDiv;
        }

        // Store annotation
        this.annotations.set(id, data);
        this.annotationObjects.set(id, {
            type: 'arrow',
            arrow: arrow,
            label: label,
            position: new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5)
        });

        return id;
    }

    /**
     * Add measurement annotation (distance, angle, area)
     */
    addMeasurementAnnotation(data) {
        const { id, measurement_type, points, value, unit, precision, label } = data;

        const group = new THREE.Group();
        group.userData.annotationId = id;

        if (measurement_type === 'distance') {
            // Draw line segments between points
            for (let i = 0; i < points.length - 1; i++) {
                const start = new THREE.Vector3(...points[i]);
                const end = new THREE.Vector3(...points[i + 1]);

                const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);
                const line = new THREE.Line(geometry, this.materials.measurement);
                group.add(line);

                // Add screen-space sized point markers
                const marker = this.createScreenSpaceMarker([start.x, start.y, start.z], 0x00ff00, 8, id);
                group.add(marker);

                if (i === points.length - 2) {
                    const endMarker = this.createScreenSpaceMarker([end.x, end.y, end.z], 0x00ff00, 8, id);
                    group.add(endMarker);
                }
            }

        } else if (measurement_type === 'angle') {
            // Draw angle with arc
            if (points.length === 3) {
                const p0 = new THREE.Vector3(...points[0]);
                const p1 = new THREE.Vector3(...points[1]); // vertex
                const p2 = new THREE.Vector3(...points[2]);

                // Draw two lines
                const geom1 = new THREE.BufferGeometry().setFromPoints([p1, p0]);
                const geom2 = new THREE.BufferGeometry().setFromPoints([p1, p2]);

                group.add(new THREE.Line(geom1, this.materials.measurement));
                group.add(new THREE.Line(geom2, this.materials.measurement));

                // Draw arc representing angle
                const v1 = new THREE.Vector3().subVectors(p0, p1).normalize();
                const v2 = new THREE.Vector3().subVectors(p2, p1).normalize();
                const angle = v1.angleTo(v2);

                const arcRadius = 0.1;
                const arcGeometry = new THREE.BufferGeometry();
                const arcPoints = [];

                for (let i = 0; i <= 32; i++) {
                    const t = i / 32;
                    const currentAngle = t * angle;

                    // Rotate v1 towards v2
                    const axis = new THREE.Vector3().crossVectors(v1, v2).normalize();
                    const rotatedVec = v1.clone().applyAxisAngle(axis, currentAngle);
                    const point = p1.clone().add(rotatedVec.multiplyScalar(arcRadius));
                    arcPoints.push(point);
                }

                arcGeometry.setFromPoints(arcPoints);
                const arc = new THREE.Line(arcGeometry, this.materials.measurement);
                group.add(arc);
            }

        } else if (measurement_type === 'area') {
            // Draw polygon outline
            const polyPoints = points.map(p => new THREE.Vector3(...p));
            polyPoints.push(new THREE.Vector3(...points[0])); // close the loop

            const geometry = new THREE.BufferGeometry().setFromPoints(polyPoints);
            const line = new THREE.Line(geometry, this.materials.measurement);
            group.add(line);
        }

        this.scene.add(group);

        // Add value label
        const labelDiv = document.createElement('div');
        labelDiv.className = 'annotation-label measurement-label';
        labelDiv.textContent = label;
        labelDiv.style.position = 'absolute';
        labelDiv.style.fontSize = '13px';
        labelDiv.style.color = '#00ff00';
        labelDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
        labelDiv.style.padding = '4px 8px';
        labelDiv.style.borderRadius = '4px';
        labelDiv.style.fontWeight = 'bold';
        labelDiv.style.pointerEvents = 'auto';
        labelDiv.style.cursor = 'pointer';

        labelDiv.addEventListener('click', () => {
            this.selectAnnotation(id);
        });

        this.labelRenderer.appendChild(labelDiv);

        // Calculate center position for label
        const center = new THREE.Vector3();
        points.forEach(p => center.add(new THREE.Vector3(...p)));
        center.divideScalar(points.length);

        // Store annotation
        this.annotations.set(id, data);
        this.annotationObjects.set(id, {
            type: 'measurement',
            group: group,
            label: labelDiv,
            position: center
        });

        return id;
    }

    /**
     * Add scale bar (2D overlay) - dynamically sized based on 3D view
     */
    addScaleBar(data) {
        const { id, length, unit, position, num_divisions } = data;

        // Create a 3D scale bar in world space
        const barGroup = new THREE.Group();
        barGroup.userData.annotationId = id;

        // Parse position or use default (place it in view)
        let barPosition;
        if (Array.isArray(position) && position.length === 3) {
            barPosition = new THREE.Vector3(...position);

            // Offset the bar position so it's not exactly on the picked point
            // Move it towards the camera by a small amount
            const toCamera = new THREE.Vector3()
                .subVectors(this.camera.position, barPosition)
                .normalize();

            // Calculate offset based on scene scale (5% of camera distance)
            const offsetDistance = this.camera.position.distanceTo(barPosition) * 0.05;
            barPosition.add(toCamera.multiplyScalar(offsetDistance));
        } else {
            // Default: place in front of camera
            const cameraTarget = new THREE.Vector3();
            this.camera.getWorldDirection(cameraTarget);
            const distance = this.camera.position.length() * 0.3;
            barPosition = cameraTarget.multiplyScalar(distance).add(this.camera.position);
        }

        barGroup.position.copy(barPosition);

        // Convert display length to world units (assuming world units are meters)
        const unitConversion = {
            'μm': 0.000001,  // micrometers to meters
            'mm': 0.001,     // millimeters to meters
            'm': 1,          // meters to meters
            'km': 1000       // kilometers to meters
        };

        const conversionFactor = unitConversion[unit] || 1;
        const barLength = length * conversionFactor; // World units in meters
        const barHeight = barLength * 0.05; // 5% of length
        const barThickness = barLength * 0.02; // 2% of length

        // Create alternating segments
        const segmentWidth = barLength / num_divisions;
        for (let i = 0; i < num_divisions; i++) {
            const color = i % 2 === 0 ? 0xffffff : 0x000000;
            const segmentGeometry = new THREE.BoxGeometry(segmentWidth, barHeight, barThickness);
            const segmentMaterial = new THREE.MeshBasicMaterial({
                color: color,
                side: THREE.DoubleSide
            });
            const segment = new THREE.Mesh(segmentGeometry, segmentMaterial);

            // Position segments along the bar
            segment.position.x = (i - num_divisions / 2 + 0.5) * segmentWidth;
            barGroup.add(segment);
        }

        // Add white border around the entire bar
        const borderGeometry = new THREE.EdgesGeometry(
            new THREE.BoxGeometry(barLength, barHeight, barThickness)
        );
        const borderMaterial = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 2 });
        const border = new THREE.LineSegments(borderGeometry, borderMaterial);
        barGroup.add(border);

        // Create text label using HTML
        const labelDiv = document.createElement('div');
        labelDiv.className = 'scale-bar-label';
        labelDiv.textContent = `${length} ${unit}`;
        labelDiv.style.position = 'absolute';
        labelDiv.style.fontSize = '14px';
        labelDiv.style.fontWeight = 'bold';
        labelDiv.style.color = 'white';
        labelDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
        labelDiv.style.padding = '4px 8px';
        labelDiv.style.borderRadius = '4px';
        labelDiv.style.pointerEvents = 'none';
        labelDiv.style.whiteSpace = 'nowrap';

        this.labelRenderer.appendChild(labelDiv);

        // Update label position to follow the bar
        const updateLabelPosition = () => {
            if (!labelDiv || !barGroup.parent) return;

            // Position label above the bar
            const labelPosition = barPosition.clone();
            labelPosition.y += barHeight * 1.5;

            const screenPos = labelPosition.clone().project(this.camera);
            const viewport = this.domElement || document.getElementById('viewport');
            const width = viewport ? viewport.clientWidth : 800;
            const height = viewport ? viewport.clientHeight : 600;

            const x = (screenPos.x + 1) * width / 2;
            const y = (-screenPos.y + 1) * height / 2;

            labelDiv.style.left = `${x}px`;
            labelDiv.style.top = `${y}px`;
            labelDiv.style.transform = 'translate(-50%, -100%)';
        };

        // Make the bar always face the camera (billboard effect)
        const updateBillboard = () => {
            if (!barGroup.parent) return;
            barGroup.quaternion.copy(this.camera.quaternion);
        };

        // Combine update functions
        const updateCallback = () => {
            updateBillboard();
            updateLabelPosition();
        };

        // Initial updates
        updateCallback();

        // Add animation callback
        if (this.engine) {
            this.engine.addAnimationCallback(updateCallback);
        }

        // Add to scene
        this.scene.add(barGroup);

        // Store annotation
        this.annotations.set(id, data);
        this.annotationObjects.set(id, {
            type: 'scale_bar',
            group: barGroup,
            label: labelDiv,
            scaleCallback: updateCallback
        });

        return id;
    }

    /**
     * Add coordinate axes
     */
    addCoordinateAxes(data) {
        const { id, origin, length, labels, colors } = data;

        const originVec = new THREE.Vector3(...origin);
        const axesGroup = new THREE.Group();
        axesGroup.userData.annotationId = id;

        const defaultColors = [
            [1, 0, 0], // X - Red
            [0, 1, 0], // Y - Green
            [0, 0, 1]  // Z - Blue
        ];

        const axisColors = colors || defaultColors;
        const directions = [
            [1, 0, 0], // X
            [0, 1, 0], // Y
            [0, 0, 1]  // Z
        ];

        // Create axes
        for (let i = 0; i < 3; i++) {
            const color = this.colorToHex(axisColors[i]);
            const dir = new THREE.Vector3(...directions[i]);

            const arrow = new THREE.ArrowHelper(
                dir,
                originVec,
                length,
                color,
                length * 0.1,
                length * 0.05
            );

            axesGroup.add(arrow);

            // Add axis label
            const labelDiv = document.createElement('div');
            labelDiv.className = 'axis-label';
            labelDiv.textContent = labels[i];
            labelDiv.style.position = 'absolute';
            labelDiv.style.fontSize = '14px';
            labelDiv.style.fontWeight = 'bold';
            labelDiv.style.color = this.colorToCSS(axisColors[i]);
            labelDiv.style.pointerEvents = 'none';

            labelDiv.dataset.axisDirection = JSON.stringify(directions[i]);
            labelDiv.dataset.axisLength = length.toString();
            labelDiv.dataset.axisOrigin = JSON.stringify(origin);

            this.labelRenderer.appendChild(labelDiv);
        }

        this.scene.add(axesGroup);

        // Store annotation
        this.annotations.set(id, data);
        this.annotationObjects.set(id, {
            type: 'coordinate_axes',
            group: axesGroup,
            origin: originVec
        });

        return id;
    }

    /**
     * Update annotation
     */
    updateAnnotation(id, updates) {
        const annotation = this.annotations.get(id);
        if (!annotation) {
            console.warn(`Annotation ${id} not found`);
            return false;
        }

        // Update annotation data
        Object.assign(annotation, updates);
        this.annotations.set(id, annotation);

        // Re-render annotation
        this.removeAnnotation(id, false);

        const type = annotation.type;
        if (type === 'text') {
            this.addTextAnnotation(annotation);
        } else if (type === 'arrow') {
            this.addArrowAnnotation(annotation);
        } else if (type === 'measurement') {
            this.addMeasurementAnnotation(annotation);
        } else if (type === 'scale_bar') {
            this.addScaleBar(annotation);
        } else if (type === 'coordinate_axes') {
            this.addCoordinateAxes(annotation);
        }

        return true;
    }

    /**
     * Remove annotation
     */
    removeAnnotation(id, removeFromMap = true) {
        const obj = this.annotationObjects.get(id);
        if (!obj) return false;

        // Remove 3D objects
        if (obj.marker) this.scene.remove(obj.marker);
        if (obj.arrow) this.scene.remove(obj.arrow);
        if (obj.group) this.scene.remove(obj.group);

        // Remove HTML labels
        if (obj.label && obj.label.parentElement) {
            obj.label.parentElement.removeChild(obj.label);
        }
        if (obj.element && obj.element.parentElement) {
            obj.element.parentElement.removeChild(obj.element);
        }

        // Remove axis labels
        if (obj.type === 'coordinate_axes') {
            const axisLabels = this.labelRenderer.querySelectorAll('.axis-label');
            axisLabels.forEach(label => {
                if (label.parentElement) {
                    label.parentElement.removeChild(label);
                }
            });
        }

        // Remove animation callbacks for marker scaling
        const callbacks = this.markerScaleCallbacks.get(id);
        if (callbacks && this.engine) {
            callbacks.forEach(callback => {
                this.engine.removeAnimationCallback(callback);
            });
            this.markerScaleCallbacks.delete(id);
        }

        // Remove scale bar animation callback
        if (obj.scaleCallback && this.engine) {
            this.engine.removeAnimationCallback(obj.scaleCallback);
        }

        this.annotationObjects.delete(id);

        if (removeFromMap) {
            this.annotations.delete(id);
        }

        return true;
    }

    /**
     * Clear all annotations
     */
    clearAll() {
        const ids = Array.from(this.annotations.keys());
        ids.forEach(id => this.removeAnnotation(id));
        return ids.length;
    }

    /**
     * Select annotation for editing
     */
    selectAnnotation(id) {
        this.selectedAnnotation = id;

        // Highlight selected annotation
        const obj = this.annotationObjects.get(id);
        if (obj && obj.label) {
            obj.label.style.outline = '2px solid yellow';
        }

        // Dispatch event
        const event = new CustomEvent('annotationSelected', { detail: { id } });
        window.dispatchEvent(event);
    }

    /**
     * Deselect annotation
     */
    deselectAnnotation() {
        if (this.selectedAnnotation) {
            const obj = this.annotationObjects.get(this.selectedAnnotation);
            if (obj && obj.label) {
                obj.label.style.outline = 'none';
            }
            this.selectedAnnotation = null;
        }
    }

    /**
     * Update label positions (call in render loop)
     */
    updateLabelPositions() {
        this.annotationObjects.forEach((obj, id) => {
            if (obj.label && obj.position) {
                const screenPos = this.toScreenPosition(obj.position);
                obj.label.style.left = `${screenPos.x}px`;
                obj.label.style.top = `${screenPos.y}px`;
            }
        });

        // Update axis labels
        const axisLabels = this.labelRenderer.querySelectorAll('.axis-label');
        axisLabels.forEach(label => {
            const dir = JSON.parse(label.dataset.axisDirection);
            const length = parseFloat(label.dataset.axisLength);
            const origin = JSON.parse(label.dataset.axisOrigin);

            const endPos = new THREE.Vector3(
                origin[0] + dir[0] * length * 1.1,
                origin[1] + dir[1] * length * 1.1,
                origin[2] + dir[2] * length * 1.1
            );

            const screenPos = this.toScreenPosition(endPos);
            label.style.left = `${screenPos.x}px`;
            label.style.top = `${screenPos.y}px`;
        });
    }

    /**
     * Convert 3D position to 2D screen coordinates
     */
    toScreenPosition(position) {
        const vector = position.clone();
        vector.project(this.camera);

        const widthHalf = this.domElement.clientWidth / 2;
        const heightHalf = this.domElement.clientHeight / 2;

        return {
            x: (vector.x * widthHalf) + widthHalf,
            y: -(vector.y * heightHalf) + heightHalf
        };
    }

    /**
     * Utility: Convert color array [r, g, b] to CSS string
     */
    colorToCSS(color) {
        const r = Math.round(color[0] * 255);
        const g = Math.round(color[1] * 255);
        const b = Math.round(color[2] * 255);
        return `rgb(${r}, ${g}, ${b})`;
    }

    /**
     * Utility: Convert color array to RGB values
     */
    colorToRGB(color) {
        return color.map(c => Math.round(c * 255));
    }

    /**
     * Utility: Convert color array to THREE.js hex
     */
    colorToHex(color) {
        const r = Math.round(color[0] * 255);
        const g = Math.round(color[1] * 255);
        const b = Math.round(color[2] * 255);
        return (r << 16) | (g << 8) | b;
    }

    /**
     * Get all annotations
     */
    getAnnotations() {
        return Array.from(this.annotations.values());
    }

    /**
     * Get annotation by ID
     */
    getAnnotation(id) {
        return this.annotations.get(id);
    }
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
    module.exports = AnnotationRenderer;
}
